import {Injectable} from '@angular/core';
import {Subject} from 'rxjs';
import {Product} from '@app/domain/product';

const DB_PREFIX = 'teki-catalog-';
const DB_VERSION = 1;
const STORE_PRODUCTS = 'products';
const STORE_META = 'meta';
const META_KEY = 'state';
/** Clave del catálogo anterior (localStorage, ~5 MB de tope). Se borra al abrir el nuevo. */
export const LEGACY_PRODUCTS_KEY = 'products_flat';

interface CatalogMeta {
    version: number | null;
    updatedAt: string;
}

type CatalogBroadcast =
    { type: 'upsert'; products: Product[] } |
    { type: 'remove'; ids: number[] } |
    { type: 'replace' } |
    { type: 'sync-request' } |
    { type: 'version'; version: number | null };

/**
 * Catálogo local de productos: copia en memoria (lo que lee la búsqueda, sin JSON.parse por tecla) persistida en
 * IndexedDB por empresa (`teki-catalog-<idEmpresa>`), sin el tope de 5 MB de localStorage.
 *
 * Todas las pestañas del navegador comparten la base; cuando una escribe, avisa a las demás por BroadcastChannel
 * para que actualicen su memoria. Si IndexedDB no está disponible (modo privado antiguo) funciona solo en memoria.
 *
 * No hace HTTP: la sincronización con el backend vive en ProductCatalogSyncService.
 */
@Injectable({
    providedIn: 'root'
})
export class ProductCatalogCacheService {

    /** Emite cuando cambia el contenido en memoria (propio o de otra pestaña). */
    readonly changed$ = new Subject<void>();
    /** Una pestaña sin datos solicita a la líder comprobar el catálogo compartido. */
    readonly syncRequested$ = new Subject<void>();

    private idEmpresa: number = null;
    private db: IDBDatabase = null;
    private channel: BroadcastChannel = null;
    private opening: Promise<void> = null;

    private productsById = new Map<number, Product>();
    private version: number | null = null;
    private loaded = false;

    private allProducts: Product[] = null;
    private salesProducts: Product[] = null;

    get empresa(): number {
        return this.idEmpresa;
    }

    get currentVersion(): number | null {
        return this.version;
    }

    get isLoaded(): boolean {
        return this.loaded;
    }

    get size(): number {
        return this.productsById.size;
    }

    /** Comprueba datos reales sin releer todo el catálogo en cada sincronización. */
    async hasData(): Promise<boolean> {
        const db = this.db;
        if (!db) {
            return this.size > 0;
        }
        const count = await requestResult<number>(db.transaction(STORE_PRODUCTS, 'readonly').objectStore(STORE_PRODUCTS).count());
        if (db !== this.db || count === 0) {
            return false;
        }
        if (this.size === 0) {
            await this.reloadFromDb();
        }
        return this.size > 0;
    }

    requestSync(): void {
        this.broadcast({type: 'sync-request'});
    }

    announceStoredCatalog(): void {
        this.broadcast({type: 'replace'});
    }

    /**
     * Productos para buscar localmente, o null si todavía no hay catálogo (el caller cae a la búsqueda HTTP).
     * Devuelve siempre el mismo arreglo mientras no cambie el catálogo: no modificarlo.
     */
    getProducts(contextoVentas: boolean = false): Product[] | null {
        if (!this.loaded || this.productsById.size === 0) {
            return null;
        }
        if (!this.allProducts) {
            this.allProducts = Array.from(this.productsById.values());
        }
        if (!contextoVentas) {
            return this.allProducts;
        }
        if (!this.salesProducts) {
            this.salesProducts = this.allProducts.filter(product => !product.ocultarEnBuscadorVentas);
        }
        return this.salesProducts;
    }

    /** Abre (o reutiliza) el catálogo de la empresa y lo carga en memoria. */
    open(idEmpresa: number): Promise<void> {
        if (this.idEmpresa === idEmpresa && this.opening) {
            return this.opening;
        }
        this.close();
        this.idEmpresa = idEmpresa;
        removeLegacyStorage();
        this.opening = this.load(idEmpresa);
        return this.opening;
    }

    close(): void {
        if (this.channel) {
            this.channel.close();
            this.channel = null;
        }
        if (this.db) {
            this.db.close();
            this.db = null;
        }
        this.idEmpresa = null;
        this.opening = null;
        this.loaded = false;
        this.version = null;
        this.productsById = new Map();
        this.invalidate();
    }

    /** Cierra y borra el catálogo guardado de la empresa (búsqueda local desactivada). */
    async purge(idEmpresa: number): Promise<void> {
        if (this.idEmpresa === idEmpresa) {
            this.close();
        }
        removeLegacyStorage();
        if (typeof indexedDB === 'undefined' || idEmpresa == null) {
            return;
        }
        await new Promise<void>(resolve => {
            try {
                const request = indexedDB.deleteDatabase(DB_PREFIX + idEmpresa);
                request.onsuccess = () => resolve();
                request.onerror = () => resolve();
                request.onblocked = () => resolve();
            } catch (e) {
                resolve();
            }
        });
    }

    /** Reemplaza el catálogo completo (snapshot). */
    async replaceAll(products: Product[], version: number): Promise<void> {
        const idEmpresa = this.idEmpresa;
        this.productsById = new Map((products || []).filter(p => p?.id != null).map(p => [Number(p.id), p]));
        this.version = version;
        this.loaded = true;
        this.invalidate();
        await this.write(tx => {
            const store = tx.objectStore(STORE_PRODUCTS);
            store.clear();
            this.productsById.forEach(product => store.put(product));
            tx.objectStore(STORE_META).put(this.meta(), META_KEY);
        });
        this.broadcast({type: 'replace'}, idEmpresa);
    }

    /** Agrega o reemplaza productos puntuales. No cambia la versión. */
    async upsert(products: Product[]): Promise<void> {
        const idEmpresa = this.idEmpresa;
        const valid = (products || []).filter(p => p?.id != null);
        if (!valid.length) {
            return;
        }
        this.applyUpsert(valid);
        await this.write(tx => {
            const store = tx.objectStore(STORE_PRODUCTS);
            valid.forEach(product => store.put(product));
        });
        this.broadcast({type: 'upsert', products: valid}, idEmpresa);
    }

    /** Actualiza campos de productos ya guardados (p. ej. orden de favoritos) sin esperar al backend. */
    async patch(changes: Array<Partial<Product> & {id: number}>): Promise<void> {
        const updated = (changes || [])
            .filter(change => this.productsById.has(Number(change.id)))
            .map(change => ({...this.productsById.get(Number(change.id)), ...change}));
        await this.upsert(updated);
    }

    async remove(ids: number[]): Promise<void> {
        const idEmpresa = this.idEmpresa;
        const valid = (ids || []).map(Number).filter(id => this.productsById.has(id));
        if (!valid.length) {
            return;
        }
        this.applyRemove(valid);
        await this.write(tx => {
            const store = tx.objectStore(STORE_PRODUCTS);
            valid.forEach(id => store.delete(id));
        });
        this.broadcast({type: 'remove', ids: valid}, idEmpresa);
    }

    async setVersion(version: number | null): Promise<void> {
        const idEmpresa = this.idEmpresa;
        this.version = version;
        await this.write(tx => tx.objectStore(STORE_META).put(this.meta(), META_KEY));
        this.broadcast({type: 'version', version}, idEmpresa);
    }

    /** Solo memoria (tests y fallback). */
    setProductsInMemory(products: Product[], version: number | null = null): void {
        this.productsById = new Map((products || []).filter(p => p?.id != null).map(p => [Number(p.id), p]));
        this.version = version;
        this.loaded = true;
        this.invalidate();
    }

    // ---- Interno ----------------------------------------------------------------------------------------------

    private async load(idEmpresa: number): Promise<void> {
        try {
            const db = await openDatabase(DB_PREFIX + idEmpresa);
            if (this.idEmpresa !== idEmpresa) {
                db.close();
                return;
            }
            this.db = db;
        } catch (e) {
            if (this.idEmpresa !== idEmpresa) {
                return;
            }
            console.warn('[catalogo] IndexedDB no disponible, catálogo solo en memoria', e);
            this.db = null;
        }
        if (this.idEmpresa !== idEmpresa) {
            return;
        }
        await this.reloadFromDb();
        if (this.idEmpresa === idEmpresa) {
            this.listenOtherTabs(idEmpresa);
        }
    }

    private async reloadFromDb(): Promise<void> {
        const db = this.db;
        if (!this.db) {
            this.loaded = true;
            return;
        }
        try {
            const [products, meta] = await Promise.all([
                requestResult<Product[]>(this.db.transaction(STORE_PRODUCTS, 'readonly').objectStore(STORE_PRODUCTS).getAll()),
                requestResult<CatalogMeta>(this.db.transaction(STORE_META, 'readonly').objectStore(STORE_META).get(META_KEY))
            ]);
            if (db !== this.db) {
                return;
            }
            this.productsById = new Map((products || []).map(p => [Number(p.id), p]));
            // Sin productos (store vaciado o borrado a medias) la versión guardada no sirve: se vuelve a descargar.
            this.version = meta && meta.version != null && this.productsById.size > 0 ? meta.version : null;
        } catch (e) {
            if (db !== this.db) {
                return;
            }
            console.error('[catalogo] Error leyendo el catálogo local', e);
            this.productsById = new Map();
            this.version = null;
        }
        this.loaded = true;
        this.invalidate();
    }

    private listenOtherTabs(idEmpresa: number): void {
        if (typeof BroadcastChannel === 'undefined') {
            return;
        }
        this.channel = new BroadcastChannel(DB_PREFIX + idEmpresa);
        this.channel.onmessage = (event: MessageEvent<CatalogBroadcast>) => {
            const message = event.data;
            if (!message || this.idEmpresa !== idEmpresa) {
                return;
            }
            switch (message.type) {
                case 'upsert':
                    this.applyUpsert(message.products);
                    break;
                case 'remove':
                    this.applyRemove(message.ids);
                    break;
                case 'version':
                    this.version = message.version;
                    break;
                case 'replace':
                    this.reloadFromDb();
                    break;
                case 'sync-request':
                    this.syncRequested$.next();
                    break;
            }
        };
    }

    private applyUpsert(products: Product[]): void {
        products.forEach(product => this.productsById.set(Number(product.id), product));
        this.invalidate();
    }

    private applyRemove(ids: number[]): void {
        ids.forEach(id => this.productsById.delete(Number(id)));
        this.invalidate();
    }

    private invalidate(): void {
        this.allProducts = null;
        this.salesProducts = null;
        this.changed$.next();
    }

    private meta(): CatalogMeta {
        return {version: this.version, updatedAt: new Date().toISOString()};
    }

    private broadcast(message: CatalogBroadcast, idEmpresa: number = this.idEmpresa): void {
        if (idEmpresa !== this.idEmpresa) {
            return;
        }
        try {
            this.channel?.postMessage(message);
        } catch (e) {
            // Otra pestaña se pondrá al día en su próxima verificación de versión.
        }
    }

    private write(fn: (tx: IDBTransaction) => void): Promise<void> {
        if (!this.db) {
            return Promise.resolve();
        }
        return new Promise<void>(resolve => {
            try {
                const tx = this.db.transaction([STORE_PRODUCTS, STORE_META], 'readwrite');
                tx.oncomplete = () => resolve();
                tx.onabort = () => {
                    console.error('[catalogo] Escritura abortada', tx.error);
                    resolve();
                };
                tx.onerror = () => undefined;
                fn(tx);
            } catch (e) {
                // La memoria ya está actualizada; la próxima verificación de versión corrige la base.
                console.error('[catalogo] Error guardando el catálogo local', e);
                resolve();
            }
        });
    }
}

function openDatabase(name: string): Promise<IDBDatabase> {
    return new Promise<IDBDatabase>((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB no soportado'));
            return;
        }
        const request = indexedDB.open(name, DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE_PRODUCTS)) {
                db.createObjectStore(STORE_PRODUCTS, {keyPath: 'id'});
            }
            if (!db.objectStoreNames.contains(STORE_META)) {
                db.createObjectStore(STORE_META);
            }
        };
        request.onsuccess = () => {
            const db = request.result;
            // Si otra versión del front necesita actualizar el esquema, se libera la conexión.
            db.onversionchange = () => db.close();
            resolve(db);
        };
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(new Error('IndexedDB bloqueado'));
    });
}

function requestResult<T>(request: IDBRequest): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        request.onsuccess = () => resolve(request.result as T);
        request.onerror = () => reject(request.error);
    });
}

/** Borra el catálogo del esquema anterior (localStorage `products_flat`). Se llama al arrancar la app. */
export function removeLegacyStorage(): void {
    try {
        localStorage.removeItem(LEGACY_PRODUCTS_KEY);
    } catch (e) {
        // Sin acceso a localStorage no hay nada que limpiar.
    }
}
